import React, { useState, useRef, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  Search,
  ChevronDown,
  SlidersHorizontal,
  Map,
  X,
  Star,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  YMaps,
  Map as YMap,
  Placemark,
  Clusterer,
  FullscreenControl,
  GeoObject,
} from "@pbe/react-yandex-maps";

import { apiUrl, mapApiKey } from "../config";
import { placemarkHotelPopup, placemarkStyles } from "./placemarkHotelPopup";
import HeaderSearchField from "../components/HeaderSearchField";
import HotelCardMobile from "../components/HotelCardMobile";
import DesktopFiltersPanel from "../components/DesktopFiltersPanel";
import searchStore from "../store/searchStore";
import HotelCard from "../components/HotelCard";
import PhotoViewer from "../components/landing/PhotoViewer";

const SORT_OPTIONS = [
  { label: "По популярности", value: "" },
  { label: "По оценке", value: "meta_rating" },
  { label: "По названию", value: "name" },
  { label: "По кол-ву звёзд", value: "star_rating" },
  { label: "По цене (возраст.)", value: "price_asc" },
  { label: "По цене (убыв.)", value: "price_desc" },
];

const HOTEL_TYPES_MAP = {
  "Все типы": "",
  Отель: "var1",
  "Гостевой дом": "var2",
  Апартаменты: "var3",
  Хостел: "var4",
  Глемпинг: "var5",
  Санаторий: "var6",
  Коттедж: "var7",
};

const SORT_MAP = {
  "По популярности": "",
  "По рейтингу": "meta_rating",
  "По цене (возраст.)": "price_asc",
  "По цене (убыв.)": "price_desc",
};

const Sprav = observer(function Sprav() {
  const navigate = useNavigate();
  const { country = "russia", region, city = "all", place = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");
  const perPage = 50;

  // Data fetching state
  const [hotels, setHotels] = useState([]);
  const [hotelsForMap, setHotelsForMap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cityData, setCityData] = useState(null);
  const [cities, setCities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [mapCoordinates, setMapCoordinates] = useState([55.751244, 37.618423]);
  const [mapZoom, setMapZoom] = useState(10);
  const [selectedHotelOnMap, setSelectedHotelOnMap] = useState(null);
  const [showMobileMap, setShowMobileMap] = useState(false);
  const mapRef = useRef(null);
  const hotelCardRefs = useRef({});
  const panelRef = useRef(null);

  // PhotoViewer state
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoViewerImages, setPhotoViewerImages] = useState([]);
  const [photoViewerIndex, setPhotoViewerIndex] = useState(0);
  const openPhotoViewer = (images, index = 0) => {
    setPhotoViewerImages(images);
    setPhotoViewerIndex(index);
    setPhotoViewerOpen(true);
  };

  // Filter states
  const [selectedType, setSelectedType] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");
  const [minRating, setMinRating] = useState(null);
  const [minStars, setMinStars] = useState(0);
  const [maxSeaDistance, setMaxSeaDistance] = useState("");
  const [withDog, setWithDog] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [filtersDropdownPos, setFiltersDropdownPos] = useState(null);
  const lastScrollY = useRef(0);
  const mobileSortDropdownRef = useRef(null);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const filtersDropdownRef = useRef(null);
  const mobileFiltersBtnRef = useRef(null);
  const desktopFiltersBtnRef = useRef(null);

  const [hiddenHeight, setHiddenHeight] = useState(0);
  const [desctopFiltersTop, setDesctopFiltersTop] = useState(80);

  // Синхронизация с searchStore при монтировании
  useEffect(() => {
    const urlCheckIn = searchParams.get("checkIn");
    const urlCheckOut = searchParams.get("checkOut");

    if (urlCheckIn) searchStore.setDateCheckin(urlCheckIn);
    if (urlCheckOut) searchStore.setDateCheckout(urlCheckOut);

    const urlAdults = searchParams.get("adults");
    if (urlAdults) searchStore.setGuestsCount(parseInt(urlAdults));

    const urlChildren = searchParams.get("children");
    if (urlChildren) {
      try {
        const childrenArray = JSON.parse(urlChildren);
        searchStore.setGuestChildren(childrenArray);
      } catch (e) {
        console.error("Error parsing children from URL:", e);
      }
    }

    const urlPet = searchParams.get("pet");
    if (urlPet === "true") searchStore.setDogFriendly(true);

    setWithDog(searchStore.dogFriendly);
  }, []);

  useEffect(() => {
    if (selectedHotelOnMap && hotelCardRefs.current[selectedHotelOnMap.id]) {
      hotelCardRefs.current[selectedHotelOnMap.id].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedHotelOnMap]);

  // Fetch hotels data
  useEffect(() => {
    const fetchHotels = async () => {
      setLoading(true);
      setError(null);

      try {
        const datein =
          searchStore.dateCheckin || searchParams.get("checkIn") || "";
        const dateout =
          searchStore.dateCheckout || searchParams.get("checkOut") || "";
        const guests = searchStore.guestsCount;
        const child = searchStore.guestsChildren;
        const dogfriendly = searchStore.dogFriendly || withDog;

        const request = {
          datein,
          dateout,
          guests,
          child,
          dogfriendly,
          place: place,
          page: currentPage,
          limit: perPage,
        };

        const response = await fetch(
          `${apiUrl}/find/${country}/${region || "all"}/${city || "all"}?${searchParams.toString()}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json;charset=utf-8" },
            body: JSON.stringify(request),
          },
        );

        const data = await response.json();

        if (data?.status === "error") throw new Error(data.message);

        const result = data?.data || {};
        setHotels(result.hotels?.list || []);
        setHotelsForMap(result.hotels?.formap || []);
        setTotalCount(result.hotels?.count || 0);
        setCityData(result.city || null);
        setCities(result.cities || []);

        if (result?.coordinates && result?.coordinates?.length === 2) {
          setMapCoordinates(result.coordinates);
          setMapZoom(result.city?.city ? 12 : 8);
        } else if (result.city?.geo_lat && result.city?.geo_lon) {
          setMapCoordinates([result.city.geo_lat, result.city.geo_lon]);
          setMapZoom(12);
        } else if (result.region?.geo_lat && result.region?.geo_lon) {
          setMapCoordinates([result.region.geo_lat, result.region.geo_lon]);
          setMapZoom(8);
        }
      } catch (err) {
        console.error("Error fetching hotels:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, [
    country,
    region,
    city,
    place,
    searchParams,
    searchStore.dateCheckin,
    searchStore.dateCheckout,
    searchStore.guestsCount,
    searchStore.guestsChildren,
    searchStore.dogFriendly,
    currentPage,
  ]);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateDimensions = () => {
      if (panelRef.current) {
        const panelH = panelRef.current.offsetHeight;
        const screenH = window.innerHeight;
        const hidden = screenH - (panelH + 80);
        const calculatedHidden = hidden < 0 ? Math.abs(hidden) : 0;
        setHiddenHeight(calculatedHidden);
      }
    };

    const resizeObserver = new ResizeObserver(() => updateDimensions());
    const observePanel = () => {
      if (panelRef.current) {
        resizeObserver.observe(panelRef.current);
        updateDimensions();
      }
    };
    const initTimeout = setTimeout(observePanel, 100);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (!ticking) {
        window.requestAnimationFrame(() => {
          let direction = "none";
          if (currentScrollY > lastScrollY + 2) direction = "down";
          else if (currentScrollY < lastScrollY - 2) direction = "up";

          if (direction === "down") {
            const newTop = (80 - hiddenHeight) * 2;
            setDesctopFiltersTop(-Math.abs(newTop));
          } else if (direction === "up") {
            setDesctopFiltersTop(80);
          }

          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    const handleResize = () => updateDimensions();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      clearTimeout(initTimeout);
      resizeObserver.disconnect();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [loading, hotels]);

  const handlePageChange = (page) => {
    const params = new URLSearchParams(searchParams);
    if (page === 1) params.delete("page");
    else params.set("page", page.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyFilters = (newFilters = {}) => {
    const params = new URLSearchParams(searchParams);

    if (searchStore.dateCheckin) params.set("checkIn", searchStore.dateCheckin);
    if (searchStore.dateCheckout)
      params.set("checkOut", searchStore.dateCheckout);
    if (searchStore.guestsCount !== 2)
      params.set("adults", searchStore.guestsCount);
    if (searchStore.guestsChildren.length > 0)
      params.set("children", JSON.stringify(searchStore.guestsChildren));
    if (searchStore.dogFriendly) params.set("pet", "true");

    params.delete("page");

    [
      "template_variant",
      "min_price",
      "max_price",
      "meta_rating",
      "star_rating",
      "distance",
      "sort_by",
      "dogfriendly",
      "amenities",
    ].forEach((key) => params.delete(key));

    if (newFilters.template_variant)
      params.set("template_variant", newFilters.template_variant);
    if (newFilters.min_price) params.set("min_price", newFilters.min_price);
    if (newFilters.max_price) params.set("max_price", newFilters.max_price);
    if (newFilters.meta_rating)
      params.set("meta_rating", newFilters.meta_rating);
    if (newFilters.star_rating)
      params.set("star_rating", newFilters.star_rating);
    if (newFilters.distance) params.set("distance", newFilters.distance);
    if (newFilters.sort_by) params.set("sort_by", newFilters.sort_by);
    if (newFilters.dogfriendly) params.set("dogfriendly", "true");
    if (newFilters.amenities) params.set("amenities", newFilters.amenities);

    setSearchParams(params);
  };

  const handleTypeChange = (typeLabel) => {
    const serverValue = HOTEL_TYPES_MAP[typeLabel] || "";
    setSelectedType(typeLabel);
    applyFilters({ template_variant: serverValue });
  };

  const handleSortChange = (sortLabel) => {
    const serverValue = SORT_MAP[sortLabel] || "";
    setSortBy(sortLabel);
    applyFilters({ sort_by: serverValue });
  };

  const handlePriceFilter = () => {
    applyFilters({ min_price: priceFrom, max_price: priceTo });
  };

  const handleRatingFilter = () => {
    applyFilters({ meta_rating: minRating, star_rating: minStars });
  };

  const handleDistanceFilter = () => {
    applyFilters({ distance: maxSeaDistance });
  };

  const handleDogFilter = () => {
    const newDogState = !withDog;
    setWithDog(newDogState);
    searchStore.setDogFriendly(newDogState);
    applyFilters({ dogfriendly: newDogState });
  };

  const toggleAmenity = (amenity) => {
    const newAmenities = selectedAmenities.includes(amenity)
      ? selectedAmenities.filter((x) => x !== amenity)
      : [...selectedAmenities, amenity];

    setSelectedAmenities(newAmenities);

    const params = new URLSearchParams(searchParams);
    params.delete("page");
    if (newAmenities.length > 0)
      params.set("amenities", newAmenities.join(","));
    else params.delete("amenities");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    setSelectedType("Все типы");
    setPriceFrom("");
    setPriceTo("");
    setMinRating(null);
    setMinStars(0);
    setMaxSeaDistance("");
    setWithDog(false);
    setSelectedAmenities([]);
    setSortBy("По популярности");

    const params = new URLSearchParams();
    const q = searchParams.get("q");
    if (q) params.set("q", q);
    if (searchStore.dateCheckin) params.set("checkIn", searchStore.dateCheckin);
    if (searchStore.dateCheckout)
      params.set("checkOut", searchStore.dateCheckout);
    if (searchStore.guestsCount !== 2)
      params.set("adults", searchStore.guestsCount);
    if (searchStore.guestsChildren.length > 0)
      params.set("children", JSON.stringify(searchStore.guestsChildren));
    if (searchStore.dogFriendly) params.set("pet", "true");

    setSearchParams(params);
    setShowFilters(false);
  };

  const handlePlacemarkClick = (e, hotel) => {
    const placemark = e.get("target");
    placemark.properties.set("balloonContent", placemarkHotelPopup(hotel));
    placemark.balloon.open();
    setSelectedHotelOnMap(hotel);
  };

  const handlePlacemarkHover = (e, hotel) => {
    const placemark = e.get("target");
    placemark.properties.set("hintContent", placemarkHotelPopup(hotel));
    setSelectedHotelOnMap(hotel);
  };

  const handlePlacemarkBlur = () => setSelectedHotelOnMap(null);

  const openFilters = (btnRef) => {
    if (!showFilters) {
      const isMobile = window.innerWidth < 640;
      const ref = isMobile
        ? btnRef || mobileFiltersBtnRef
        : desktopFiltersBtnRef;
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const dropdownWidth = isMobile ? window.innerWidth - 16 : 560;
        let left = isMobile ? 8 : rect.left;
        if (left + dropdownWidth > window.innerWidth - 8)
          left = window.innerWidth - dropdownWidth - 8;
        if (left < 8) left = 8;
        setFiltersDropdownPos({
          top: rect.bottom + 4,
          left,
          width: dropdownWidth,
          isMobile,
        });
      }
    }
    setShowFilters((v) => !v);
  };

  useEffect(() => {
    function handleOutside(e) {
      if (
        mobileSortDropdownRef.current &&
        !mobileSortDropdownRef.current.contains(e.target)
      )
        setMobileSortOpen(false);
      const inDropdown =
        filtersDropdownRef.current &&
        filtersDropdownRef.current.contains(e.target);
      const inDesktopBtn =
        desktopFiltersBtnRef.current &&
        desktopFiltersBtnRef.current.contains(e.target);
      const inMobileBtn =
        mobileFiltersBtnRef.current &&
        mobileFiltersBtnRef.current.contains(e.target);
      if (!inDropdown && !inDesktopBtn && !inMobileBtn) setShowFilters(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // Фикс дёрганья фильтров: lockUntil 320 мс + порог без -5
  useEffect(() => {
    let lockUntil = 0;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (!searchStore.isOnFocus) {
        const now = Date.now();
        if (now >= lockUntil) {
          if (currentY > lastScrollY.current + 5) {
            setFiltersVisible(false);
            lockUntil = now + 320;
          } else if (currentY < lastScrollY.current) {
            setFiltersVisible(true);
            lockUntil = now + 320;
          }
        }
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const filtered = hotels;

  const renderMap = () => (
    <>
      <style>{placemarkStyles}</style>
      <YMaps query={{ apikey: mapApiKey }}>
        <YMap
          defaultState={{ center: mapCoordinates, zoom: mapZoom }}
          className="w-full h-full"
          instanceRef={mapRef}
          onClick={() => {
            setSelectedHotelOnMap(null);
            if (mapRef.current) mapRef.current.balloon.close();
          }}
        >
          <Clusterer
            options={{
              preset: "islands#invertedVioletClusterIcons",
              groupByCoordinates: false,
            }}
            modules={["geoObject.addon.balloon", "geoObject.addon.hint"]}
          >
            {hotelsForMap.map((hotel) => (
              <Placemark
                key={hotel._id || hotel.id}
                geometry={[hotel.coordinates?.[0], hotel.coordinates?.[1]]}
                options={{
                  iconColor:
                    selectedHotelOnMap?.id === hotel.id ? "red" : "blue",
                  hideIconOnBalloonOpen: false,
                  openEmptyHint: true,
                }}
                onClick={(e) => handlePlacemarkClick(e, hotel)}
                onHover={(e) => handlePlacemarkHover(e, hotel)}
                onMouseLeave={handlePlacemarkBlur}
              />
            ))}
          </Clusterer>

          {cities.length > 0 &&
            cities.map((city) => (
              <GeoObject
                key={city._id}
                geometry={{
                  type: "Point",
                  coordinates: [city.geo_lat, city.geo_lon],
                }}
                properties={{ iconContent: city.city }}
                options={{
                  preset: "islands#blueStretchyIcon",
                  draggable: false,
                }}
                onClick={() => {
                  navigate(`/${country}/${city.region_url}/${city.city_url}`);
                }}
              />
            ))}

          <FullscreenControl options={{ position: { top: 20, left: 10 } }} />
        </YMap>
      </YMaps>
    </>
  );

  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / perPage);
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages, start + maxVisible - 1);
      if (end - start + 1 < maxVisible)
        start = Math.max(1, end - maxVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    };

    return (
      <div className="flex items-center justify-center gap-2 mt-6 mb-8 px-4">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {getPageNumbers().map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
              page === currentPage
                ? "bg-blue-600 text-white"
                : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  };

  const renderFiltersContent = () => (
    <div className={filtersDropdownPos?.isMobile ? "p-4 space-y-4" : "p-5"}>
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Тип объекта
        </p>
        <div className="flex flex-wrap gap-1.5">
          {Object.keys(HOTEL_TYPES_MAP).map((typeLabel) => (
            <button
              key={typeLabel}
              onClick={() => handleTypeChange(typeLabel)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                selectedType === typeLabel
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-100"
              }`}
            >
              {typeLabel}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Цена за ночь, ₽
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={priceFrom}
            onChange={(e) => setPriceFrom(e.target.value)}
            placeholder="от"
            className="w-full h-8 rounded-lg border border-gray-200 px-2 text-sm text-gray-700 outline-none focus:border-blue-400"
          />
          <span className="text-gray-400 text-sm flex-shrink-0">—</span>
          <input
            type="number"
            value={priceTo}
            onChange={(e) => setPriceTo(e.target.value)}
            placeholder="до"
            className="w-full h-8 rounded-lg border border-gray-200 px-2 text-sm text-gray-700 outline-none focus:border-blue-400"
          />
          <button
            onClick={handlePriceFilter}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
          >
            OK
          </button>
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Оценка, от {minRating || "—"}
        </p>
        <input
          type="range"
          min={0}
          max={5}
          step={0.1}
          value={minRating || 0}
          onChange={(e) => setMinRating(Number(e.target.value))}
          className="w-full"
        />
        <button
          onClick={handleRatingFilter}
          className="mt-2 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
        >
          Применить
        </button>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Количество звёзд
        </p>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} onClick={() => setMinStars(minStars === s ? 0 : s)}>
              <Star
                className={`w-7 h-7 transition-colors ${
                  s <= minStars
                    ? "text-yellow-400 fill-yellow-400"
                    : "text-gray-300 fill-gray-100"
                }`}
              />
            </button>
          ))}
        </div>
        <button
          onClick={handleRatingFilter}
          className="mt-2 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
        >
          Применить
        </button>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Удалённость от {cityData?.sea ? "моря" : "центра"}
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={maxSeaDistance}
            onChange={(e) => setMaxSeaDistance(e.target.value)}
            placeholder="500"
            className="w-24 h-8 rounded-lg border border-gray-200 px-2 text-sm text-gray-700 outline-none focus:border-blue-400"
          />
          <span className="text-sm text-gray-500">метров</span>
          <button
            onClick={handleDistanceFilter}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
          >
            OK
          </button>
        </div>
      </div>

      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={withDog}
            onChange={handleDogFilter}
            className="w-4 h-4 text-blue-600"
          />
          <span className="text-sm text-gray-700">Можно с собакой</span>
        </label>
      </div>

      {filtersDropdownPos?.isMobile ? (
        <div className="sticky bottom-0 flex items-center justify-between -mx-4 px-4 -mb-4 pb-4 pt-3 bg-white border-t border-gray-100">
          <button
            onClick={handleResetFilters}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
          >
            <X className="w-3 h-3" />
            Сбросить всё
          </button>
          <button
            onClick={() => setShowFilters(false)}
            className="px-6 py-2 rounded-full text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors ml-auto"
          >
            Применить
          </button>
        </div>
      ) : (
        <button
          onClick={handleResetFilters}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
        >
          <X className="w-3 h-3" />
          Сбросить всё
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Search header */}
      <div
        className="sticky top-0 z-50"
        style={{
          background: "linear-gradient(135deg, #020c1b 0%, #0d2137 100%)",
        }}
      >
        {/* Mobile header */}
        <div className="sm:hidden px-2 py-1 bg-[#2b6cc4]">
          <HeaderSearchField
            initialDestination={initialQ}
            searchSubtitle={
              totalCount > 0 ? `${totalCount} отелей` : "12 027 отелей"
            }
          />
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-center gap-2 px-6 py-1 bg-[#2b7ad4]">
          <div className="flex-1 max-w-3xl">
            <HeaderSearchField initialDestination={initialQ} />
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="max-w-2xl mx-auto mt-10 bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">Ошибка загрузки данных</p>
          <p className="text-red-500 text-sm mt-2">{error}</p>
        </div>
      )}

      {/* Hotels list */}
      {!loading && !error && (
        <>
          {/* Filters row - mobile — maxHeight вместо translateY, top 100 */}
          <div
            className="sm:hidden sticky z-40 bg-white"
            style={{
              top: 100,
              maxHeight: filtersVisible ? "64px" : "0px",
              overflow: filtersVisible ? "visible" : "hidden",
              borderBottom: filtersVisible ? "1px solid #f3f4f6" : "none",
              boxShadow: filtersVisible
                ? "0 1px 2px 0 rgb(0 0 0 / 0.05)"
                : "none",
              transition: "max-height 300ms ease",
            }}
          >
            <div className="px-2 py-2 flex items-center gap-2">
              <div
                className="relative flex-shrink-0"
                ref={mobileSortDropdownRef}
              >
                <button
                  onClick={() => setMobileSortOpen((v) => !v)}
                  className="h-8 flex items-center gap-1.5 px-3 rounded-full text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  {SORT_OPTIONS.find((o) => o.value === sortBy)?.label ||
                    "По популярности"}
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </button>
                {mobileSortOpen && (
                  <div className="absolute left-0 top-full mt-1 z-[10005] bg-white border border-gray-200 rounded-xl shadow-xl py-1 min-w-[200px]">
                    {SORT_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        onClick={() => handleSortChange(o.label)}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          sortBy === o.value
                            ? "text-blue-600 font-semibold bg-blue-50"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="w-px h-5 bg-gray-200 flex-shrink-0" />
              <button
                ref={mobileFiltersBtnRef}
                onClick={() => openFilters(mobileFiltersBtnRef)}
                className={`h-8 flex items-center gap-1.5 px-3 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${
                  showFilters
                    ? "border-blue-500 text-blue-600 bg-blue-50"
                    : "border-gray-200 text-gray-600 bg-white hover:bg-gray-100"
                }`}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Все фильтры
              </button>
              <button
                className="h-8 w-8 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0 ml-auto"
                onClick={() => setShowMobileMap(true)}
              >
                <Map className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="max-w-full mx-auto px-2 sm:px-6 pt-2 sm:pt-4">
            <div className="flex gap-4">
              {/* Desktop filters sidebar */}
              <div className="hidden md:block w-56 flex-shrink-0">
                <div
                  className="sticky"
                  ref={panelRef}
                  style={{ top: `${desctopFiltersTop}px` }}
                >
                  <DesktopFiltersPanel
                    cityData={cityData}
                    selectedType={selectedType}
                    setSelectedType={handleTypeChange}
                    priceFrom={priceFrom}
                    setPriceFrom={setPriceFrom}
                    priceTo={priceTo}
                    setPriceTo={setPriceTo}
                    minRating={minRating}
                    setMinRating={setMinRating}
                    minStars={minStars}
                    setMinStars={setMinStars}
                    maxSeaDistance={maxSeaDistance}
                    setMaxSeaDistance={setMaxSeaDistance}
                    withDog={withDog}
                    setWithDog={setWithDog}
                    selectedAmenities={selectedAmenities}
                    toggleAmenity={toggleAmenity}
                    onResetAmenities={() => setSelectedAmenities([])}
                    sortBy={sortBy}
                    setSortBy={handleSortChange}
                    onApplyFilters={applyFilters}
                    onResetFilters={handleResetFilters}
                  />
                </div>
              </div>

              {/* Hotel list */}
              <div className="w-full lg:w-[600px] xl:w-[680px]">
                {filtered.length === 0 ? (
                  <div className="bg-white rounded-xl p-12 text-center text-gray-400 shadow-sm">
                    <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">
                      Объекты не найдены. Попробуйте изменить фильтры.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2 sm:space-y-3">
                      {filtered.map((hotel) => (
                        <div
                          key={hotel._id || hotel.id}
                          ref={(el) => (hotelCardRefs.current[hotel.id] = el)}
                          className={`hidden sm:block transition-all duration-300 ${
                            selectedHotelOnMap?.id === hotel.id
                              ? "ring-2 ring-blue-500 ring-offset-2 rounded-xl shadow-lg"
                              : ""
                          }`}
                          onMouseEnter={() => setSelectedHotelOnMap(hotel)}
                          onMouseLeave={() => setSelectedHotelOnMap(null)}
                        >
                          <HotelCard
                            hotel={hotel}
                            onPhotoClick={openPhotoViewer}
                          />
                        </div>
                      ))}

                      {filtered.map((hotel) => (
                        <div
                          key={hotel._id || hotel.id}
                          className={`sm:hidden transition-all duration-300 ${
                            selectedHotelOnMap?.id === hotel.id
                              ? "ring-2 ring-blue-500 ring-offset-2 rounded-xl shadow-lg"
                              : ""
                          }`}
                        >
                          <HotelCardMobile
                            hotel={hotel}
                            onPhotoClick={openPhotoViewer}
                          />
                        </div>
                      ))}
                    </div>

                    <Pagination />
                  </>
                )}
              </div>

              {/* Map - desktop */}
              <div className="hidden lg:block flex-1 flex-shrink-0">
                <div className="sticky top-[5rem]">
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="relative h-[calc(100vh-6rem)]">
                      {renderMap()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile map modal */}
      {showMobileMap && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 lg:hidden flex flex-col"
          onClick={() => setShowMobileMap(false)}
        >
          <div
            className="bg-white m-4 mt-16 rounded-2xl overflow-hidden flex-1 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="font-semibold text-gray-800 text-sm">
                Карта объектов
              </span>
              <button
                onClick={() => setShowMobileMap(false)}
                className="p-1.5 rounded-full hover:bg-gray-100"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="relative flex-1">{renderMap()}</div>
          </div>
        </div>
      )}

      {/* Filters dropdown */}
      {showFilters && filtersDropdownPos && (
        <div
          ref={filtersDropdownRef}
          className="fixed z-[9999] bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-y-auto"
          style={{
            top: filtersDropdownPos.top,
            left: filtersDropdownPos.left,
            width: filtersDropdownPos.width,
            maxHeight: `calc((100vh - ${filtersDropdownPos.top + 8}px) * 0.85)`,
          }}
        >
          {renderFiltersContent()}
        </div>
      )}

      {/* PhotoViewer */}
      <PhotoViewer
        images={photoViewerImages}
        initialIndex={photoViewerIndex}
        open={photoViewerOpen}
        onOpenChange={setPhotoViewerOpen}
      />
    </div>
  );
});

export default Sprav;
